/*eslint complexity: off*/
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment';
import dig from 'object-dig';

import ReceiptIcon from '@material-ui/icons/Receipt';

import Paper from '~/components/Paper';
import GridItem from '~/components/GridItem';
import InputShow from '~/components/InputShow';
import ModalDelete from '~/components/ModalDelete';
import PaperButtons from '~/components/PaperButtons';
import GridContainer from '~/components/GridContainer';

import Request from '~/providers/Request';
import PinRecord from '~/records/PinRecord';
import { AbilityContext } from '~/contexts/Can';

import { historyPropType } from '~/config/routerPropTypes';
import { pins, createPin } from '~/config/routes';
import { pinStatusOptions } from '~/config/constants';

import Context from './context';

const attrName = (attr) => PinRecord.hummanAttributeName(attr);

function ShowPin({ history }) {
  const { id } = history.location.route.getParams();
  const { records, addRecords } = useContext(Context);
  const [record, setRecord] = useState(new PinRecord());
  const { t } = useTranslation();
  const [loading, setLoading] = useState(!records || !records[id]);
  const [modal, setModal] = useState(false);
  const ability = useContext(AbilityContext);

  useEffect(() => {
    if (records && records[id]) {
      setRecord(record.updateAttributes(records[id]));
    }
    setLoading(true);
    Request.get(`/admin/pins/${id}`, {})
      .then((response) => {
        setLoading(false);
        addRecords({ data: [response.data] });
        setRecord(record.updateAttributes(response.data));
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
      });
  }, []);

  async function handleDelete() {
    setLoading(true);
    Request.del(`/admin/pins/${id}`)
      .then(() => {
        toast.success(t('delete-toast-success'));
        history.push(pins.spec);
      })
      .catch(() => {
        setLoading(false);
        toast.error(t('delete-toast-fail'));
      });
    setModal(false);
  }

  return (
    <Paper
      Icon={ReceiptIcon}
      iconColor="blue"
      title={t('pins.defSingular')}
      loading={loading}
      actions={
        <PaperButtons
          id={id}
          setModal={ability.can('delete', 'Pins') ? setModal : null}
          backRoute={pins}
          newRoute={ability.can('new', 'Pins') ? createPin : null}
          editRoute={ability.can('edit', 'Pins') ? null : null}
        />
      }
    >
      <GridContainer>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow label={attrName('code')} value={record.get('code')} />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow label={attrName('pan')} value={record.get('pan')} />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow label={attrName('serial')} value={record.get('serial')} />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow
            type="object"
            digObject={['name']}
            label={attrName('user')}
            value={record.get('user')}
          />
        </GridItem>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow
            type="object"
            digObject={['description']}
            label={attrName('product')}
            value={record.get('product')}
          />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow
            label={attrName('status')}
            value={pinStatusOptions[record.get('status')]}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow
            type="boolean"
            label={attrName('is_active')}
            value={record.get('is_active')}
          />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow label={attrName('batch')} value={record.get('batch')} />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow
            type="object"
            digObject={['quantity']}
            label={attrName('product_quantity')}
            value={record.get('product')}
          />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow
            label={attrName('quantity_used')}
            value={record.get('quantity_used') || '0'}
          />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow
            label={attrName('quantity_pending')}
            value={
              dig(record.get('product'), 'quantity') -
              record.get('quantity_used')
            }
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow
            label={attrName('activated_at')}
            value={
              record.get('activated_at')
                ? moment(record.get('activated_at')).format(
                    'DD/MM/YYYY HH:mm:ss'
                  )
                : ''
            }
          />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow
            label={attrName('desactived_at')}
            value={
              record.get('desactived_at')
                ? moment(record.get('desactived_at')).format(
                    'DD/MM/YYYY HH:mm:ss'
                  )
                : ''
            }
          />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow
            label={attrName('expires_at')}
            value={
              record.get('expires_at')
                ? moment(record.get('expires_at')).format('DD/MM/YYYY HH:mm:ss')
                : ''
            }
          />
        </GridItem>
      </GridContainer>

      <ModalDelete
        modal={modal}
        onClose={() => {
          setModal(false);
        }}
        name={record.get('name')}
        id={record.get('id')}
        deleteAction={handleDelete}
      />
    </Paper>
  );
}

ShowPin.propTypes = {
  history: historyPropType,
};

export default ShowPin;
